// src/pages/FastKenoGamePage.jsx - Complete with separate tabs
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from "react";
import { useNavigate } from "react-router-dom";
import PageWrapper from "../components/PageWrapper";
import KenoBall from "../components/KenoBall";
import BraunClock from "../components/BraunClock";
import axiosInstance from "../axiosInstance";
import { LanguageContext } from "../contexts/LanguageContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const FastKenoGamePage = (props) => {
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);

  // State declarations
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [drawnNumbers, setDrawnNumbers] = useState([]);
  const [currentDrawBall, setCurrentDrawBall] = useState(null);
  const [sessionStatus, setSessionStatus] = useState("waiting");
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [myTickets, setMyTickets] = useState([]);
  const [stakeAmount, setStakeAmount] = useState(50);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("game");
  const [gameHistory, setGameHistory] = useState([]);
  const [gameResults, setGameResults] = useState([]);
  const [statistics, setStatistics] = useState({ totalGames: 0, totalWins: 0, totalWinAmount: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawProgress, setDrawProgress] = useState(0);
  const [currentGameId, setCurrentGameId] = useState(null);
  const [topWinners, setTopWinners] = useState([]);
  const [recentDraws, setRecentDraws] = useState([]);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [showWinToast, setShowWinToast] = useState(false);
  const [lastWinAmount, setLastWinAmount] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [rotateCurrentBall, setRotateCurrentBall] = useState(false);
  const [gameEndMessage, setGameEndMessage] = useState(null);
  const [hasFetchedResults, setHasFetchedResults] = useState(false);
  const [showPrizeTable, setShowPrizeTable] = useState(true);
  const [maxTickets, setMaxTickets] = useState(2);
  const [expandedDraws, setExpandedDraws] = useState({});

  // Refs
  const timerIntervalRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastBallRef = useRef(null);
  const lastGameIdRef = useRef(null);
  const toastTimeoutRef = useRef(null);

  const TOTAL_NUMBERS = 80;
  const MAX_NUMBERS = 10;
  const allowedStakes = [10, 20, 50, 100, 200, 500, 1000];

  const prizeMultipliers = {
    10: {
      10: 500,
      9: 100,
      8: 50,
      7: 20,
      6: 8,
      5: 3,
      4: 1.5,
      3: 0.5,
      2: 0,
      1: 0,
      0: 0,
    },
  };

  // Toggle draw expansion
  const toggleDrawExpand = (drawId) => {
    setExpandedDraws((prev) => ({
      ...prev,
      [drawId]: !prev[drawId],
    }));
  };

  // Translations for 5 languages
  const t = (key) => {
    const translations = {
      en: {
        title: "FAST KENO",
        gameId: "Game ID",
        timeRemaining: "TIME REMAINING",
        players: "PLAYERS",
        myTickets: "MY TICKETS",
        selectNumbers: "Select 10 Numbers",
        quick: "QUICK",
        clear: "CLEAR",
        addTicket: "ADD TICKET",
        drawInProgress: "🎯 DRAW IN PROGRESS",
        progress: "PROGRESS",
        drawnNumbers: "DRAWN NUMBERS",
        gameCompleted: "GAME COMPLETED",
        youWon: "YOU WON",
        betterLuck: "Better luck next time!",
        newGameStarting: "Next game starting soon...",
        active: "ACTIVE",
        won: "WON",
        lost: "LOST",
        bet: "Bet",
        numbers: "numbers",
        matches: "Matches",
        history: "HISTORY",
        results: "RESULTS",
        stats: "STATS",
        leaderboard: "LEADERBOARD",
        totalGames: "GAMES",
        winRate: "WIN RATE",
        wins: "WINS",
        totalWinAmount: "TOTAL WIN",
        winners: "WINNERS",
        noTickets: "No tickets yet",
        selectHint: "Select 10 numbers and click ADD TICKET",
        loading: "Loading...",
        pleaseWait: "Please wait for the game to start",
        drawInProgressMsg: "Draw in progress, cannot select numbers",
        gameCompletedMsg: "Game completed, next game starting soon",
        maxNumbers: "You must select exactly 10 numbers",
        selectExactly: "Select exactly",
        numbersToPick: "numbers",
        cannotAddTicket: "Cannot add ticket at this time",
        ticketAdded: "Ticket added!",
        failedAddTicket: "Failed to add ticket",
        youWonToast: "You Won!",
        currentBall: "🎯 CURRENT BALL",
        prizeTable: "🏆 PRIZE TABLE",
        hidePrizes: "Hide Prizes",
        showPrizes: "Show Prizes",
        multiplier: "x",
        picks: "Picks",
        possibleWin: "Possible Win",
        ticketsRemaining: "Tickets remaining",
        maxTicketsReached: "Maximum 2 tickets per game",
        yourTickets: "Your Tickets",
        id: "ID",
        showMore: "Show all 20 numbers",
        showLess: "Show less",
        yourStats: "Your Statistics",
        topWinners: "Top Winners",
        noWinnersYet: "No winners yet",
        rank: "Rank",
        player: "Player",
        amount: "Amount",
        recentDraws: "Recent Draws",
        noDrawsYet: "No draws yet",
        drawNumber: "Draw",
      },
      am: {
        title: "ፈጣን ኬኖ",
        gameId: "የጨዋታ መታወቂያ",
        timeRemaining: "የቀረ ጊዜ",
        players: "ተጫዋቾች",
        myTickets: "የኔ ቲኬቶች",
        selectNumbers: "10 ቁጥሮች ምረጡ",
        quick: "ፈጣን",
        clear: "አጽዳ",
        addTicket: "ቲኬት ጨምሩ",
        drawInProgress: "🎯 እጣ በሂደት ላይ",
        progress: "ሂደት",
        drawnNumbers: "የወጡ ቁጥሮች",
        gameCompleted: "ጨዋታ ተጠናቋል",
        youWon: "አሸንፈዋል",
        betterLuck: "በሚቀጥለው ጊዜ መልካም እድል!",
        newGameStarting: "ቀጣይ ጨዋታ ይጀምራል...",
        active: "ንቁ",
        won: "አሸንፏል",
        lost: "ተሸንፏል",
        bet: "ውርርድ",
        numbers: "ቁጥሮች",
        matches: "ተዛማጅ",
        history: "ታሪክ",
        results: "ውጤቶች",
        stats: "ስታቲስቲክስ",
        leaderboard: "ከፍተኛ አሸናፊዎች",
        totalGames: "ጨዋታዎች",
        winRate: "የማሸነፍ መጠን",
        wins: "ድሎች",
        totalWinAmount: "ጠቅላላ ሽልማት",
        winners: "አሸናፊዎች",
        noTickets: "ምንም ቲኬት የለም",
        selectHint: "10 ቁጥሮች ይምረጡ እና ቲኬት ጨምሩ",
        loading: "በመጫን ላይ...",
        pleaseWait: "እባክዎ ጨዋታው እስኪጀመር ይጠብቁ",
        drawInProgressMsg: "እጣ በሂደት ላይ ነው, ቁጥሮች መምረጥ አይቻልም",
        gameCompletedMsg: "ጨዋታው ተጠናቋል, ቀጣዩ ጨዋታ ይጀምራል",
        maxNumbers: "በትክክል 10 ቁጥሮች መምረጥ አለብዎት",
        selectExactly: "በትክክል ይምረጡ",
        numbersToPick: "ቁጥሮች",
        cannotAddTicket: "በዚህ ጊዜ ቲኬት መጨመር አይቻልም",
        ticketAdded: "ቲኬት ተጨምሯል!",
        failedAddTicket: "ቲኬት መጨመር አልተሳካም",
        youWonToast: "አሸንፈዋል!",
        currentBall: "🎯 አሁን የወጣው ኳስ",
        prizeTable: "🏆 የሽልማት ሰንጠረዥ",
        hidePrizes: "ሽልማቶች ደብቅ",
        showPrizes: "ሽልማቶች አሳይ",
        multiplier: "x",
        picks: "ምርጫ",
        possibleWin: "የሚቻል ሽልማት",
        ticketsRemaining: "የቀሩ ቲኬቶች",
        maxTicketsReached: "በአንድ ጨዋታ ከፍተኛው 2 ቲኬቶች",
        yourTickets: "የእርስዎ ቲኬቶች",
        id: "መታወቂያ",
        showMore: "ሁሉንም 20 ቁጥሮች አሳይ",
        showLess: "አጭር አሳይ",
        yourStats: "የእርስዎ ስታቲስቲክስ",
        topWinners: "ከፍተኛ አሸናፊዎች",
        noWinnersYet: "እስካሁን አሸናፊ የለም",
        rank: "ደረጃ",
        player: "ተጫዋች",
        amount: "መጠን",
        recentDraws: "የቅርብ ጊዜ እጣዎች",
        noDrawsYet: "እስካሁን እጣ አልተካሄደም",
        drawNumber: "እጣ",
      },
      or: {
        title: "KEEENO SASSIISAA",
        gameId: "ID Peericha",
        timeRemaining: "YEROON HAFE",
        players: "Taphattootaa",
        myTickets: "Tikkeetii Koo",
        selectNumbers: "Lakkoofsa 10 Filadhu",
        quick: "SASSIISAA",
        clear: "Qulqulleessi",
        addTicket: "Tikkeetii Dabali",
        drawInProgress: "🎯 Argannoo Irratti",
        progress: "Itooffii",
        drawnNumbers: "Lakkoofsa Ba'an",
        gameCompleted: "Peerichiin Xumurame",
        youWon: "MOEFFATTAN",
        betterLuck: "Yeroo ittaanu carraa gaarii!",
        newGameStarting: "Peerichiin ittaanu eegala...",
        active: "Hojiirraa",
        won: "Mo'ate",
        lost: "Dhuwufte",
        bet: "Saantima",
        numbers: "lakkoofsa",
        matches: "Walqixxummaa",
        history: "SEENA",
        results: "BU'AA",
        stats: "Gabatee",
        leaderboard: "MO'ATTOOTA OLI",
        totalGames: "Peerichota",
        winRate: "Moo'ina 'Hanxina",
        wins: "Mo'annoowwan",
        totalWinAmount: "Mo'annaa Waliigalaa",
        winners: "Mo'attoota",
        noTickets: "Tikkeettiin hin jiru",
        selectHint: "Lakkoofsa 10 filadhuu fi tikkeetii dabali",
        loading: "Fe'amaa jira...",
        pleaseWait: "Mee peerichiin eegaluusaa eegaa",
        drawInProgressMsg: "Argannoo irratti, lakkoofsi filamuu hin danda'u",
        gameCompletedMsg: "Peerichaan xumurame, peerichiin ittaanu eegala",
        maxNumbers: "Lakkoofsa 10 sirriitti filachuu qabdu",
        selectExactly: "Sirriitti filadhu",
        numbersToPick: "lakkoofsa",
        cannotAddTicket: "Yeroo kana tikkeetii dabaluu hin danda'u",
        ticketAdded: "Tikkeetiin dabalamte!",
        failedAddTicket: "Tikkeetiin dabalamuu hin dandeenye",
        youWonToast: "Mo'atte!",
        currentBall: "🎯 Kubbayyaa Ammaa",
        prizeTable: "🏆 Gabatee Mo'annoowwan",
        hidePrizes: "Mo'annonni Dhoksaa",
        showPrizes: "Mo'annonni Addaansaa",
        multiplier: "x",
        picks: "Filannoo",
        possibleWin: "Mo'anna Raawwa'amaa",
        ticketsRemaining: "Tikkeetii hafte",
        maxTicketsReached: "Peerichiidhaaf tikkeetii 2 qofa",
        yourTickets: "Tikkeettiin Keessan",
        id: "ID",
        showMore: "Lakkoofsa 20 hunda agarsiisi",
        showLess: "Xiqqeessa agarsiisi",
        yourStats: "Gabatee Keessan",
        topWinners: "Mo'attoota Olii",
        noWinnersYet: "Mo'attoonni hin jiran",
        rank: "Sadarkaa",
        player: "Taphattoo",
        amount: "Qaba",
        recentDraws: "Argannoo Dhumaa",
        noDrawsYet: "Argannoon hin jiru",
        drawNumber: "Argannoo",
      },
      ti: {
        title: "ቅልጡፍ ኬኖ",
        gameId: "ምልላይ ጸወታ",
        timeRemaining: "እተርፍ ግዜ",
        players: "ተጻወትቲ",
        myTickets: "ቲከታተይ",
        selectNumbers: "10 ቁጽሪታት ምረጽ",
        quick: "ቅልጡፍ",
        clear: "ኣጽረይ",
        addTicket: "ቲከት ምሓል",
        drawInProgress: "🎯 ዕጽብ ኣብ ምሃልሃል",
        progress: "ምሃልሃል",
        drawnNumbers: "ዝወጹ ቁጽሪታት",
        gameCompleted: "ጸወታ ተዛዚሙ",
        youWon: "ተሰናኢልካ",
        betterLuck: "ኣብ ዚቕጽል ግዜ ጽቡቕ ዕድል!",
        newGameStarting: "ዚቕጽል ጸወታ ክጅምር እዩ...",
        active: "ንጡፍ",
        won: "ተሰናኢሉ",
        lost: "ተሓሊመ",
        bet: "ተካፈለ",
        numbers: "ቁጽሪታት",
        matches: "ዚተመዓረበ",
        history: "ታሪኽ",
        results: "ውጽኢታት",
        stats: "ስታቲስቲክሳት",
        leaderboard: "ላዕለዎት ተሓተንቲ",
        totalGames: "ጸወታታት",
        winRate: "ምዕወታ",
        wins: "ዕወታታት",
        totalWinAmount: "ብላቴና",
        winners: "ተሓተንቲ",
        noTickets: "ቲከት የለን",
        selectHint: "10 ቁጽሪታት ምረጽ ከምኡውን ቲከት ምሓል",
        loading: "ይጽዓን ኣሎ...",
        pleaseWait: "በጃኻ ጸወታ ክጅምር ምትኣሳሰር",
        drawInProgressMsg: "ዕጽብ ኣብ ምሃልሃል እዩ, ቁጽሪታት ክትምርጽ ኣይከኣልን",
        gameCompletedMsg: "ጸወታ ተዛዚሙ, ዚቕጽል ጸወታ ክጅምር እዩ",
        maxNumbers: "ብትክክል 10 ቁጽሪታት ክትምርጽ ኣለካ",
        selectExactly: "ብትክክል ምረጽ",
        numbersToPick: "ቁጽሪታት",
        cannotAddTicket: "ኣብዚ ግዜ ቲከት ምሓል ኣይከኣልን",
        ticketAdded: "ቲከት ተወሲኹ!",
        failedAddTicket: "ቲከት ምሓል ኣይተኻኣለን",
        youWonToast: "ተሰናኢልካ!",
        currentBall: "🎯 ሕጂ ዝወጸ ኳስ",
        prizeTable: "🏆 ሰንጠረዝ",
        hidePrizes: "ሽልማታት ምስዓር",
        showPrizes: "ሽልማታት ምርኣይ",
        multiplier: "x",
        picks: "ምርጫ",
        possibleWin: "ዚከኣል ሽልማት",
        ticketsRemaining: "ዝተረፉ ቲከታት",
        maxTicketsReached: "ኣብ ሓደ ጸወታ ከፍተኛ 2 ቲከታት",
        yourTickets: "ቲከታትኩም",
        id: "መለለዪ",
        showMore: "ኩሎም 20 ቁጽሪታት ኣርኣይ",
        showLess: "ሓጺሩ ኣርኣይ",
        yourStats: "ስታቲስቲክሳትኩም",
        topWinners: "ላዕለዎት ተሓተንቲ",
        noWinnersYet: "ሕጂ ተሓታንቲ የለዉን",
        rank: "ደረጃ",
        player: "ተጻዋታይ",
        amount: "ብዝሒ",
        recentDraws: "ዳሕረዎት ዕጽብታት",
        noDrawsYet: "ዕጽብታት ኣይተካየዱን",
        drawNumber: "ዕጽብ",
      },
      wo: {
        title: "KEENO KEENIYA",
        gameId: "ID Gitaa",
        timeRemaining: "WODIYA AYSUWAA",
        players: "Asooggidiyaa",
        myTickets: "Tikiitiya taa",
        selectNumbers: "10 Kiishshatu Doora",
        quick: "KEENIYA",
        clear: "Mulqqa",
        addTicket: "Tikiiti Ossa",
        drawInProgress: "🎯 Kusuwaa Karatida",
        progress: "Wonna",
        drawnNumbers: "Kiishsha Biyuwa",
        gameCompleted: "Gitaa Amarsiyo",
        youWon: "Wontitta",
        betterLuck: "Bollaara woojjaa maynni!",
        newGameStarting: "Bollaara gitaa baddana...",
        active: "Ossanawka",
        won: "Wonttis",
        lost: "Yels",
        bet: "Xaaphuwa",
        numbers: "kiishsha",
        matches: "Mallanchiyaa",
        history: "Sinttena",
        results: "Bollaara",
        stats: "Haggarraa",
        leaderboard: "WONTTIDAA ASAAR",
        totalGames: "Gitaa",
        winRate: "Wonna Wontta",
        wins: "Wontsiyaa",
        totalWinAmount: "Wonttidu Wozzaara",
        winners: "Wonttidaa",
        noTickets: "Tikiiti Aybakk'o",
        selectHint: "10 Kiishshatu Dooraa, tikiiti ossa",
        loading: "Buxxuwaagade...",
        pleaseWait: "Huu gitaa baddanaagaa koyye",
        drawInProgressMsg: "Kusuwaa karatida, kiishshi dooran danddayeenna",
        gameCompletedMsg: "Gitaa amarsiyo, bollaara gitaa baddana",
        maxNumbers: "Kuma 10 kiishshi doorana doshshi",
        selectExactly: "Ubbaateni doora",
        numbersToPick: "kiishsha",
        cannotAddTicket: "Awudiyani tikiiti ossiya danddayeenna",
        ticketAdded: "Tikiiti ossiys!",
        failedAddTicket: "Tikiiti ossiya danddayennabeem",
        youWonToast: "Wontitta!",
        currentBall: "🎯 Haashuwoolan Bola",
        prizeTable: "🏆 Shilimatu Xaafaa",
        hidePrizes: "Shilima Uuma",
        showPrizes: "Shilima Be'eyyiza",
        multiplier: "x",
        picks: "Dooramaa",
        possibleWin: "Danddayicc Shilima",
        ticketsRemaining: "Haarid Tikiitiya",
        maxTicketsReached: "Gitaa Ikkatu ayfu 2 tikiitiya",
        yourTickets: "Hurta Tikiitiya",
        id: "ID",
        showMore: "20 Kiishshee Kuma Be'eyyiza",
        showLess: "Shiiccishsha Be'eyyiza",
        yourStats: "Hurta Haggarraa",
        topWinners: "Wonttidaa Asaar",
        noWinnersYet: "Wonttidaa Aybakk'o",
        rank: "Darejaa",
        player: "Asooggidaa",
        amount: "Gitaa",
        recentDraws: "Bollaara Kusuwaa",
        noDrawsYet: "Kusuwai Bollaara Aybakk'o",
        drawNumber: "Kusuwaa",
      },
    };
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const getMultiplier = (matchCount) => prizeMultipliers[10]?.[matchCount] || 0;
  const getWinAmount = (matchCount, stake) => stake * getMultiplier(matchCount);

  // Fetch user statistics from backend
  const fetchUserStats = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/keno/user-stats");
      if (response.data.success) {
        setStatistics({
          totalGames: Number(response.data.stats.totalGames) || 0,
          totalWins: Number(response.data.stats.totalWins) || 0,
          totalWinAmount: Number(response.data.stats.totalWinAmount) || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching user stats:", error);
    }
  }, []);

  // Fetch top winners from backend
  const fetchTopWinners = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/keno/top-winners");
      if (response.data.success) {
        const winners = response.data.winners || [];
        const sanitizedWinners = winners.map(winner => ({
          ...winner,
          amount: Number(winner?.amount) || Number(winner?.total_wins) || 0,
        }));
        setTopWinners(Array.isArray(sanitizedWinners) ? sanitizedWinners : []);
      } else {
        setTopWinners([]);
      }
    } catch (error) {
      console.error("Error fetching top winners:", error);
      setTopWinners([]);
    }
  }, []);

  // Fetch recent draws for results tab (full game objects with drawn numbers)
  const fetchRecentDraws = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/keno/last-results");
      if (response.data.success) {
        // Get the last 10 games with their drawn numbers
        const [games] = await axiosInstance.get("/keno/history");
        if (games.data.success) {
          const completedGames = games.data.games
            .filter(game => game.status === "completed" && game.drawn_numbers && game.drawn_numbers.length > 0)
            .slice(0, 10);
          setRecentDraws(completedGames);
        }
      }
    } catch (error) {
      console.error("Error fetching recent draws:", error);
      setRecentDraws([]);
    }
  }, []);

  // Alternative: Fetch recent draws directly from database
  const fetchRecentDrawsDirect = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/keno/recent-draws");
      if (response.data.success) {
        setRecentDraws(response.data.draws || []);
      } else {
        // Fallback to history
        const historyResponse = await axiosInstance.get("/keno/history");
        if (historyResponse.data.success) {
          const completedGames = historyResponse.data.games
            .filter(game => game.status === "completed" && game.drawn_numbers && game.drawn_numbers.length > 0)
            .slice(0, 10);
          setRecentDraws(completedGames);
        }
      }
    } catch (error) {
      console.error("Error fetching recent draws:", error);
      // Fallback: use history
      try {
        const historyResponse = await axiosInstance.get("/keno/history");
        if (historyResponse.data.success) {
          const completedGames = historyResponse.data.games
            .filter(game => game.status === "completed" && game.drawn_numbers && game.drawn_numbers.length > 0)
            .slice(0, 10);
          setRecentDraws(completedGames);
        }
      } catch (e) {
        setRecentDraws([]);
      }
    }
  }, []);

  // Server-synced timer
  const startServerSyncedTimer = useCallback((serverRemaining) => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    let currentRemaining = serverRemaining;
    setTimeRemaining(currentRemaining);

    timerIntervalRef.current = setInterval(() => {
      if (currentRemaining > 0 && sessionStatus === "waiting") {
        currentRemaining--;
        setTimeRemaining(currentRemaining);
      } else if (currentRemaining <= 0) {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      }
    }, 1000);
  }, [sessionStatus]);

  // Fetch active session
  const fetchActiveSession = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/keno/active-session");
      if (response.data.success) {
        const newStatus = response.data.status;
        const newGameId = response.data.gameId;

        if (lastGameIdRef.current && lastGameIdRef.current !== newGameId) {
          resetForNewGame();
        }
        lastGameIdRef.current = newGameId;

        setTotalPlayers(response.data.totalPlayers || 0);
        setCurrentGameId(newGameId);
        setSessionStatus(newStatus);
        setMaxTickets(response.data.maxTicketsPerUser || 2);

        if (newStatus === "waiting") {
          const serverRemaining = response.data.timeRemaining || 0;
          startServerSyncedTimer(serverRemaining);
        } else {
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          setTimeRemaining(0);
        }

        if (newStatus === "drawing") {
          setIsDrawing(true);
          setGameCompleted(false);
          
          if (response.data.drawnNumbers && Array.isArray(response.data.drawnNumbers)) {
            const newDrawnNumbers = response.data.drawnNumbers;
            
            if (newDrawnNumbers.length > drawnNumbers.length && newDrawnNumbers.length > 0) {
              const newBall = newDrawnNumbers[newDrawnNumbers.length - 1];
              if (newBall !== lastBallRef.current) {
                lastBallRef.current = newBall;
                setCurrentDrawBall(newBall);
                setRotateCurrentBall(true);
                setTimeout(() => setRotateCurrentBall(false), 600);
              }
            }
            
            setDrawnNumbers(newDrawnNumbers);
            setDrawProgress((newDrawnNumbers.length / 20) * 100);
            
            setMyTickets(prev => prev.map(ticket => ({
              ...ticket,
              matchedNumbers: ticket.numbers.filter(n => newDrawnNumbers.includes(n)),
              matchCount: ticket.numbers.filter(n => newDrawnNumbers.includes(n)).length
            })));
          }
          
          if (response.data.currentBall && response.data.currentBall !== lastBallRef.current) {
            lastBallRef.current = response.data.currentBall;
            setCurrentDrawBall(response.data.currentBall);
          }
        } else if (newStatus === "completed") {
          setIsDrawing(false);
          if (!hasFetchedResults && !gameCompleted) {
            setHasFetchedResults(true);
            await fetchResults();
          }
        } else if (newStatus === "waiting") {
          setIsDrawing(false);
          if (gameCompleted) resetForNewGame();
        }

        if (response.data.tickets && Array.isArray(response.data.tickets)) {
          const formattedTickets = response.data.tickets.map((ticket) => ({
            ticketIndex: ticket.ticketIndex,
            numbers: ticket.selectedNumbers,
            stake: ticket.stakeAmount,
            numbersCount: 10,
            gameId: ticket.gameId,
            status: "ACTIVE",
            matchedNumbers: drawnNumbers.filter((n) =>
              ticket.selectedNumbers?.includes(n),
            ),
            matchCount:
              ticket.selectedNumbers?.filter((n) => drawnNumbers.includes(n))
                .length || 0,
            winAmount: 0,
          }));
          setMyTickets(formattedTickets);
        }
      }
    } catch (error) {
      console.error("Error fetching session:", error);
    }
  }, [drawnNumbers, gameCompleted, hasFetchedResults, startServerSyncedTimer]);

  const fetchResults = async () => {
    try {
      const response = await axiosInstance.get("/keno/results");
      if (response.data.success && response.data.hasResults) {
        const totalWin = response.data.summary?.totalWin || 0;
        
        if (totalWin > 0) {
          setGameEndMessage({ type: "win", amount: totalWin });
          setLastWinAmount(totalWin);
          setShowWinToast(true);
          if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
          toastTimeoutRef.current = setTimeout(() => setShowWinToast(false), 5000);
        } else {
          setGameEndMessage({ type: "lose" });
        }
        setGameCompleted(true);

        fetchGameHistory();
        fetchUserStats();

        if (response.data.results && Array.isArray(response.data.results)) {
          setMyTickets((prev) => {
            const updatedTickets = prev.map((ticket) => {
              const result = response.data.results.find(
                (r) =>
                  String(r.gameId) === String(ticket.gameId) ||
                  String(r.ticketIndex) === String(ticket.ticketIndex),
              );
              if (result) {
                return {
                  ...ticket,
                  winAmount: result.winAmount,
                  matchCount: result.matchCount,
                  multiplier: result.multiplier,
                  status: result.winAmount > 0 ? "WON" : "LOST",
                };
              }
              return { ...ticket, status: "LOST", winAmount: 0 };
            });
            return updatedTickets;
          });
        }

        setTimeout(() => {
          if (!isDrawing && gameCompleted) {
            resetForNewGame();
            fetchActiveSession();
          }
        }, 5000);
      } else {
        setTimeout(() => {
          setHasFetchedResults(false);
          fetchActiveSession();
        }, 2000);
      }
    } catch (error) {
      console.error("Error fetching results:", error);
      setTimeout(() => {
        setHasFetchedResults(false);
        fetchActiveSession();
      }, 2000);
    }
  };

  const fetchGameHistory = async () => {
    try {
      const response = await axiosInstance.get("/keno/history");
      if (response.data.success) {
        setGameHistory(response.data.games);
        const wins = response.data.games.filter((g) => g.win_amount > 0).length;
        const totalWin = response.data.games.reduce((sum, g) => sum + (g.win_amount || 0), 0);
        setStatistics({
          totalGames: response.data.games.length,
          totalWins: wins,
          totalWinAmount: totalWin,
        });
      }
    } catch (error) {
      console.error("Error fetching game history:", error);
    }
  };

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/user/profile");
      if (response.data.success) {
        localStorage.setItem("userId", response.data.user.id);
      }
    } catch (error) {
      if (error.response?.status === 401) navigate("/login");
    }
  }, [navigate]);

  const resetForNewGame = useCallback(() => {
    setSelectedNumbers([]);
    setDrawnNumbers([]);
    setCurrentDrawBall(null);
    setIsDrawing(false);
    setDrawProgress(0);
    setGameCompleted(false);
    setRotateCurrentBall(false);
    setGameEndMessage(null);
    setHasFetchedResults(false);
    setSessionStatus("waiting");
    setMyTickets([]);
    lastBallRef.current = null;
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
  }, []);

  // Initialize data on mount
  useEffect(() => {
    const initializeData = async () => {
      await fetchUserProfile();
      await fetchGameHistory();
      await fetchUserStats();
      await fetchTopWinners();
      await fetchRecentDrawsDirect();
      await fetchActiveSession();
      setIsInitialized(true);
    };
    initializeData();
  }, []);

  // Poll for updates
  useEffect(() => {
    if (!isInitialized) return;
    
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    
    const intervalTime = isDrawing ? 1000 : 3000;
    
    pollIntervalRef.current = setInterval(() => {
      fetchActiveSession();
      if (activeTab === "stats") {
        fetchUserStats();
      }
      if (activeTab === "leaderboard") {
        fetchTopWinners();
      }
      if (activeTab === "results") {
        fetchRecentDrawsDirect();
      }
    }, intervalTime);
    
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [fetchActiveSession, isInitialized, isDrawing, activeTab, fetchUserStats, fetchTopWinners, fetchRecentDrawsDirect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const canSelectNumbers =
    sessionStatus === "waiting" &&
    !isDrawing &&
    !gameCompleted &&
    myTickets.length < maxTickets;

  const toggleNumber = (num) => {
    if (!canSelectNumbers) {
      if (myTickets.length >= maxTickets) {
        toast.info(t("maxTicketsReached"));
      } else if (sessionStatus !== "waiting") {
        toast.info(t("pleaseWait"));
      } else if (isDrawing) {
        toast.info(t("drawInProgressMsg"));
      } else if (gameCompleted) {
        toast.info(t("gameCompletedMsg"));
      }
      return;
    }

    setSelectedNumbers((prev) => {
      if (prev.includes(num)) {
        return prev.filter((n) => n !== num);
      }
      if (prev.length >= MAX_NUMBERS) {
        toast.warning(t("maxNumbers"));
        return prev;
      }
      return [...prev, num].sort((a, b) => a - b);
    });
  };

  const quickPick = () => {
    if (!canSelectNumbers) {
      toast.info(t("pleaseWait"));
      return;
    }
    const nums = new Set();
    while (nums.size < MAX_NUMBERS) nums.add(Math.floor(Math.random() * 80) + 1);
    setSelectedNumbers(Array.from(nums).sort((a, b) => a - b));
  };

  const clearSelections = () => {
    if (!canSelectNumbers) {
      toast.info(t("pleaseWait"));
      return;
    }
    setSelectedNumbers([]);
  };

  const addTicket = async () => {
    if (!canSelectNumbers) {
      if (myTickets.length >= maxTickets) {
        toast.error(t("maxTicketsReached"));
      } else {
        toast.error(t("cannotAddTicket"));
      }
      return;
    }
    if (selectedNumbers.length !== MAX_NUMBERS) {
      toast.error(`${t("selectExactly")} ${MAX_NUMBERS} ${t("numbersToPick")}`);
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post("/keno/join", {
        stakeAmount,
        selectedNumbers,
      });

      if (response.data.success) {
        const newTicket = {
          ticketIndex: response.data.ticket?.ticketIndex,
          gameId: response.data.ticket?.gameId,
          numbers: [...selectedNumbers],
          stake: stakeAmount,
          numbersCount: 10,
          status: "ACTIVE",
          matchedNumbers: [],
          matchCount: 0,
          winAmount: 0,
        };
        setMyTickets((prev) => [newTicket, ...prev]);
        setSelectedNumbers([]);
        toast.success(`${t("ticketAdded")} #${response.data.ticket?.ticketIndex}`);
        await fetchActiveSession();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t("failedAddTicket"));
    } finally {
      setLoading(false);
    }
  };

  const getDisplayGameId = () => {
    if (!currentGameId) return "------";
    return String(currentGameId).slice(-9);
  };

  // Render functions
  const renderPrizeTable = () => {
    const currentMultipliers = prizeMultipliers[10];
    if (!currentMultipliers) return null;

    const prizeRows = [];
    for (let matches = 10; matches >= 1; matches--) {
      const multiplier = currentMultipliers[matches];
      if (multiplier !== undefined) {
        prizeRows.push(
          <div key={matches} className="flex justify-between items-center text-[10px] py-0.5 border-b border-gray-700/30">
            <span className="text-gray-400 w-8 text-left">{matches}</span>
            <span className="text-orange-400 font-bold w-12 text-center">{multiplier}x</span>
            <span className="text-green-400 w-16 text-right">{(stakeAmount * multiplier).toFixed(0)} ETB</span>
          </div>
        );
      }
    }

    return (
      <div className="bg-gray-900/80 rounded-lg p-2 mb-2">
        <div className="flex justify-between items-center mb-2">
          <p className="text-orange-400 text-[10px] font-semibold">{t("prizeTable")} (10 {t("picks")} | {stakeAmount} ETB)</p>
          <button onClick={() => setShowPrizeTable(!showPrizeTable)} className="text-gray-400 text-[8px] hover:text-orange-400">
            {showPrizeTable ? "▲" : "▼"}
          </button>
        </div>
        {showPrizeTable && (
          <div className="bg-gray-800/30 rounded-lg p-2">
            <div className="flex justify-between text-[9px] font-bold text-gray-500 pb-1 border-b border-gray-700">
              <span className="w-8 text-left">{t("matches")}</span>
              <span className="w-12 text-center">{t("multiplier")}</span>
              <span className="w-16 text-right">{t("possibleWin")}</span>
            </div>
            {prizeRows}
          </div>
        )}
      </div>
    );
  };

  const renderNumbers = () => {
    if (isDrawing || gameCompleted) return null;

    const rows = [];
    const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
    const numbersPerRow = isMobile ? 8 : 10;
    const totalRows = Math.ceil(TOTAL_NUMBERS / numbersPerRow);
    for (let row = 0; row < totalRows; row++) {
      const cells = [];
      for (let col = 0; col < numbersPerRow; col++) {
        const num = row * numbersPerRow + col + 1;
        if (num > TOTAL_NUMBERS) break;
        const isSelected = selectedNumbers.includes(num);
        cells.push(
          <KenoBall
            key={num}
            number={num}
            size="xxs"
            isSelected={isSelected}
            onClick={toggleNumber}
          />
        );
      }
      rows.push(
        <div key={row} className="flex justify-center gap-1 mb-1 flex-wrap">
          {cells}
        </div>
      );
    }
    return rows;
  };

  const renderDrawAnimation = () => {
    if (!isDrawing) return null;
    const displayBall = currentDrawBall || (drawnNumbers.length > 0 ? drawnNumbers[drawnNumbers.length - 1] : null);
    const isMobile = typeof window !== "undefined" && window.innerWidth < 640;

    const getDrawnNumbersRows = () => {
      const actualDrawnNumbers = drawnNumbers.filter((num) => num !== currentDrawBall);
      if (actualDrawnNumbers.length === 0) return null;

      const ballsPerRow = isMobile ? 7 : 10;
      const rows = [];

      for (let i = 0; i < actualDrawnNumbers.length; i += ballsPerRow) {
        const rowBalls = actualDrawnNumbers.slice(i, i + ballsPerRow);
        rows.push(
          <div key={i} className={`flex gap-1 mb-1 ${isMobile ? "justify-center" : "justify-start"}`}>
            {rowBalls.map((num, idx) => (
              <KenoBall key={`${i}-${idx}`} number={num} size="xs" isDrawn={true} />
            ))}
          </div>
        );
      }
      return <div>{rows}</div>;
    };

    return (
      <div className="bg-gray-900/80 rounded-xl p-3 mb-3">
        <div className="text-center mb-2">
          <div className="text-orange-400 text-[10px] font-semibold mb-1">{t("drawInProgress")}</div>
          <div className="flex justify-center items-center min-h-[100px] perspective-1000">
            {displayBall ? (
              <div className={`${rotateCurrentBall ? "animate-tumble" : ""} ball-pulse`}>
                <KenoBall number={displayBall} size="lg" isCurrent={true} shouldRotate={true} />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center animate-pulse">
                <span className="text-gray-400 text-xl">?</span>
              </div>
            )}
          </div>
          <p className="text-orange-500/50 text-[8px] mt-1">{t("currentBall")}</p>
        </div>
        <div className="mb-2">
          <div className="flex justify-between text-gray-400 text-[10px] mb-0.5">
            <span>{t("progress")}</span>
            <span className="text-orange-400">{drawnNumbers.length}/20</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1">
            <div className="bg-gradient-to-r from-orange-500 to-yellow-500 h-1 rounded-full transition-all duration-500" style={{ width: `${drawProgress}%` }} />
          </div>
        </div>
        <div>
          <p className="text-gray-400 text-[10px] mb-1">{t("drawnNumbers")}</p>
          <div className="max-h-32 overflow-y-auto">{getDrawnNumbersRows()}</div>
        </div>
      </div>
    );
  };

  const renderGameCompletion = () => {
    if (!gameCompleted) return null;
    const isWin = gameEndMessage?.type === "win";
    const winAmount = gameEndMessage?.amount || 0;
    return (
      <div className="bg-gray-900/80 rounded-xl p-3 mb-3 text-center">
        <div className="text-3xl mb-1">{isWin ? "🏆" : "😢"}</div>
        <div className={`font-bold text-sm ${isWin ? "text-green-400" : "text-gray-400"}`}>
          {isWin ? `${t("youWon")} ${winAmount.toLocaleString()} ETB!` : t("gameCompleted")}
        </div>
        {!isWin && <div className="text-gray-500 text-[10px]">{t("betterLuck")}</div>}
        <div className="text-gray-600 text-[10px] mt-1">{t("newGameStarting")}</div>
      </div>
    );
  };

  const renderTicketsGrid = () => {
    if (myTickets.length === 0 && !isDrawing && !gameCompleted) {
      return (
        <div className="text-center py-2 text-gray-500 text-[11px] bg-gray-800/30 rounded-lg">
          🎫 {t("noTickets")}<br />
          <span className="text-[9px]">{t("selectHint")}</span>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {myTickets.map((ticket, idx) => {
          const isWinner = ticket.status === "WON" || ticket.winAmount > 0;
          const isLost = ticket.status === "LOST";
          const winAmountValue = ticket.winAmount > 0 ? ticket.winAmount : getWinAmount(ticket.matchCount, ticket.stake);
          const multiplierValue = ticket.multiplier || getMultiplier(ticket.matchCount);

          return (
            <div key={idx} className={`bg-gray-800/50 rounded-lg p-2 ${isWinner ? "border-l-3 border-green-500" : isLost ? "border-l-3 border-red-500" : "border-l-3 border-yellow-500"}`}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-orange-400 text-[11px] font-bold">#{ticket.ticketIndex || idx + 1}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${isWinner ? "bg-green-500/20 text-green-400" : isLost ? "bg-red-500/20 text-red-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                  {isWinner ? `${t("won")} +${winAmountValue.toFixed(0)}` : isLost ? t("lost") : t("active")}
                </span>
              </div>
              <div className="grid grid-cols-5 gap-0.5 mb-1">
                {ticket.numbers.map((num, i) => {
                  const isMatched = ticket.matchedNumbers?.includes(num) ||
                    (ticket.matchCount > 0 && ticket.numbers.includes(num) && drawnNumbers.includes(num));
                  return (
                    <div key={i} className={`flex items-center justify-center py-0.5 rounded-md text-[10px] font-bold ${isMatched && (isDrawing || isWinner) ? "bg-green-600 text-white" : isWinner && !isDrawing ? "bg-green-500/30 text-green-400" : "bg-gray-700/50 text-gray-300"}`}>
                      {num}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-[8px] pt-1 border-t border-gray-700/50">
                <span className="text-gray-500">10 {t("numbers")}</span>
                <span className="text-gray-400">{ticket.stake} ETB</span>
              </div>
              {(ticket.matchCount > 0 || isWinner) && (
                <div className="text-[8px] text-center mt-1">
                  {isWinner ? (
                    <span className="text-green-400 font-bold">{ticket.matchCount}/10 × {multiplierValue}x = +{winAmountValue.toFixed(0)} ETB</span>
                  ) : isDrawing && ticket.matchCount > 0 ? (
                    <span className="text-green-400 animate-pulse">{ticket.matchCount} {t("matches")} → {getWinAmount(ticket.matchCount, ticket.stake).toFixed(0)} ETB</span>
                  ) : ticket.matchCount > 0 ? (
                    <span className="text-yellow-400">{ticket.matchCount}/10 {t("matches")}</span>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (!isInitialized) {
    return (
      <PageWrapper {...props}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-gray-400 text-xs">{t("loading")}</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper {...props}>
      <ToastContainer position="top-right" autoClose={2000} theme="dark" style={{ zIndex: 9999 }} />

      <div className="relative z-0 pt-2 pb-4 px-3 sm:px-4 pointer-events-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-white to-orange-400 bg-clip-text text-transparent">
              {t("title")}
            </h1>
            <p className="text-gray-400 text-xs sm:text-sm">{t("id")}: {getDisplayGameId()}</p>
          </div>

          {!isDrawing && !gameCompleted && renderPrizeTable()}

          {/* Timer Display */}
          {sessionStatus === "waiting" && !isDrawing && !gameCompleted && (
            <div className="flex justify-center mb-4">
              <BraunClock countdown={timeRemaining} gameStatus={sessionStatus} />
            </div>
          )}

          {renderDrawAnimation()}
          {renderGameCompletion()}

          {/* Number Selection */}
          {!isDrawing && !gameCompleted && (
            <div className="bg-gray-900/80 rounded-xl p-3 mb-3">
              <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
                <div className="flex gap-2 flex-wrap">
                  <select
                    value={stakeAmount}
                    onChange={(e) => setStakeAmount(Number(e.target.value))}
                    disabled={!canSelectNumbers}
                    className={`bg-gray-800 text-white px-3 py-1.5 rounded-lg text-sm border border-gray-700 ${!canSelectNumbers ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {allowedStakes.map((amt) => (
                      <option key={amt} value={amt}>{amt} ETB</option>
                    ))}
                  </select>
                  <button onClick={quickPick} disabled={!canSelectNumbers} className={`bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold ${!canSelectNumbers ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-700"}`}>
                    🎲 {t("quick")}
                  </button>
                  <button onClick={clearSelections} disabled={!canSelectNumbers} className={`bg-gray-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold ${!canSelectNumbers ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-600"}`}>
                    🗑 {t("clear")}
                  </button>
                </div>
                <div className="bg-orange-500/20 rounded-lg px-3 py-1.5">
                  <p className="text-orange-400 font-bold text-base">{selectedNumbers.length}/{MAX_NUMBERS}</p>
                </div>
              </div>

              <div className="overflow-x-auto">{renderNumbers()}</div>

              <button
                onClick={addTicket}
                disabled={loading || selectedNumbers.length !== MAX_NUMBERS || !canSelectNumbers}
                className={`w-full mt-3 py-2.5 rounded-xl font-bold text-sm transition-all ${
                  loading || selectedNumbers.length !== MAX_NUMBERS || !canSelectNumbers
                    ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
                }`}
              >
                {loading ? t("loading") : `➕ ${t("addTicket")} - ${stakeAmount} ETB`}
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="bg-gray-900/80 rounded-xl">
            <div className="flex overflow-x-auto border-b border-gray-700/50">
              <button onClick={() => setActiveTab("game")} className={`flex-1 py-2 px-3 font-semibold text-xs whitespace-nowrap transition-all ${activeTab === "game" ? "text-orange-400 border-b-2 border-orange-400" : "text-gray-500"}`}>
                {t("myTickets")}
              </button>
              <button onClick={() => setActiveTab("history")} className={`flex-1 py-2 px-3 font-semibold text-xs whitespace-nowrap transition-all ${activeTab === "history" ? "text-orange-400 border-b-2 border-orange-400" : "text-gray-500"}`}>
                {t("history")}
              </button>
              <button onClick={() => setActiveTab("results")} className={`flex-1 py-2 px-3 font-semibold text-xs whitespace-nowrap transition-all ${activeTab === "results" ? "text-orange-400 border-b-2 border-orange-400" : "text-gray-500"}`}>
                {t("results")}
              </button>
              <button onClick={() => setActiveTab("stats")} className={`flex-1 py-2 px-3 font-semibold text-xs whitespace-nowrap transition-all ${activeTab === "stats" ? "text-orange-400 border-b-2 border-orange-400" : "text-gray-500"}`}>
                {t("stats")}
              </button>
              <button onClick={() => setActiveTab("leaderboard")} className={`flex-1 py-2 px-3 font-semibold text-xs whitespace-nowrap transition-all ${activeTab === "leaderboard" ? "text-orange-400 border-b-2 border-orange-400" : "text-gray-500"}`}>
                {t("leaderboard")}
              </button>
            </div>
            <div className="p-3">
              {activeTab === "game" && (
                <div>
                  <p className="text-gray-400 text-[10px] mb-2">📋 {t("yourTickets")} ({myTickets.length}/{maxTickets})</p>
                  {renderTicketsGrid()}
                </div>
              )}
              {activeTab === "history" && (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {gameHistory.length === 0 ? (
                    <div className="text-center text-gray-500 text-xs py-4">{t("noTickets")}</div>
                  ) : (
                    gameHistory.slice(0, 10).map((game) => (
                      <div key={game.id} className="bg-gray-800 rounded-md p-2 flex justify-between items-center text-[11px] hover:bg-gray-700 transition-colors">
                        <span className="text-orange-400 font-mono font-bold">#{game.id}</span>
                        <span className="text-gray-300">{game.selected_numbers?.length || 10} {t("numbers")}</span>
                        <span className="text-gray-300">{game.stake_amount} ETB</span>
                        <span className={`font-bold ${game.win_amount > 0 ? "text-green-400" : "text-red-400"}`}>
                          {game.win_amount > 0 ? `+${game.win_amount}` : game.win_amount} ETB
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
              {activeTab === "results" && (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  <p className="text-gray-400 text-[10px] mb-2">🎲 {t("recentDraws")} ({recentDraws.length}/10)</p>
                  {Array.isArray(recentDraws) && recentDraws.length > 0 ? (
                    recentDraws.map((draw, idx) => {
                      const isExpanded = expandedDraws[draw.id || idx];
                      const drawnNumbersArray = draw.drawn_numbers || [];
                      const displayNumbers = isExpanded ? drawnNumbersArray : drawnNumbersArray.slice(0, 10);
                      const hasMore = drawnNumbersArray.length > 10;
                      
                      return (
                        <div key={draw.id || idx} className="bg-gray-800 rounded-lg p-3 hover:bg-gray-700/50 transition-colors">
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-orange-400 font-mono font-bold text-xs">
                                {t("drawNumber")} #{draw.id || idx + 1}
                              </span>
                              <span className="text-gray-500 text-[9px]">
                                {draw.created_at ? new Date(draw.created_at).toLocaleString() : ''}
                              </span>
                            </div>
                            <button 
                              onClick={() => toggleDrawExpand(draw.id || idx)}
                              className="text-gray-400 text-[9px] hover:text-orange-400 transition-colors"
                            >
                              {isExpanded ? "▲ " + t("showLess") : "▼ " + t("showMore")}
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-1 justify-center">
                            {displayNumbers.map((num, i) => (
                              <span 
                                key={i} 
                                className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500/30 to-orange-600/30 text-orange-400 flex items-center justify-center text-[10px] font-bold border border-orange-500/50"
                              >
                                {num}
                              </span>
                            ))}
                          </div>
                          {hasMore && !isExpanded && (
                            <div className="text-center mt-2">
                              <span className="text-gray-500 text-[8px]">+{drawnNumbersArray.length - 10} more numbers</span>
                            </div>
                          )}
                          {draw.win_amount > 0 && (
                            <div className="mt-2 text-center">
                              <span className="text-green-400 text-[9px] font-semibold">🏆 Jackpot Winner!</span>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center text-gray-500 text-xs py-8 bg-gray-800 rounded-md">
                      {t("noDrawsYet")}
                    </div>
                  )}
                </div>
              )}
              {activeTab === "stats" && (
                <div>
                  <p className="text-gray-400 text-[10px] mb-2">📊 {t("yourStats")}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-gray-800 rounded-md p-2 text-center">
                      <p className="text-gray-400 text-[8px]">{t("totalGames")}</p>
                      <p className="text-white text-base font-bold">{statistics?.totalGames || 0}</p>
                    </div>
                    <div className="bg-gray-800 rounded-md p-2 text-center">
                      <p className="text-gray-400 text-[8px]">{t("winRate")}</p>
                      <p className="text-green-400 text-base font-bold">
                        {statistics?.totalGames > 0 
                          ? ((statistics?.totalWins / statistics?.totalGames) * 100).toFixed(1) 
                          : 0}%
                      </p>
                    </div>
                    <div className="bg-gray-800 rounded-md p-2 text-center">
                      <p className="text-gray-400 text-[8px]">{t("wins")}</p>
                      <p className="text-green-400 text-base font-bold">{statistics?.totalWins || 0}</p>
                    </div>
                    <div className="bg-gray-800 rounded-md p-2 text-center">
                      <p className="text-gray-400 text-[8px]">{t("totalWinAmount")}</p>
                      <p className="text-orange-400 text-base font-bold">
                        {(statistics?.totalWinAmount || 0).toFixed(0)} ETB
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === "leaderboard" && (
                <div>
                  <p className="text-gray-400 text-[10px] mb-2">🏆 {t("topWinners")}</p>
                  {Array.isArray(topWinners) && topWinners.length > 0 ? (
                    <div className="space-y-1 max-h-96 overflow-y-auto">
                      {topWinners.map((winner, idx) => {
                        let amount = 0;
                        if (winner) {
                          if (typeof winner.amount === 'number') {
                            amount = winner.amount;
                          } else if (typeof winner.amount === 'string') {
                            amount = parseFloat(winner.amount) || 0;
                          } else if (typeof winner.total_wins === 'number') {
                            amount = winner.total_wins;
                          } else if (typeof winner.total_wins === 'string') {
                            amount = parseFloat(winner.total_wins) || 0;
                          }
                        }
                        
                        return (
                          <div key={idx} className="bg-gray-800 rounded-md p-2 flex justify-between items-center text-[11px] hover:bg-gray-700 transition-colors">
                            <div className="flex items-center gap-3">
                              <span className={`font-bold w-8 ${idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-gray-400' : idx === 2 ? 'text-orange-400' : 'text-gray-500'}`}>
                                #{idx + 1}
                              </span>
                              <span className="text-gray-300 truncate max-w-[150px]">
                                {winner?.username || winner?.full_name || 'Player'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-green-400 font-bold">
                                {amount.toFixed(0)} ETB
                              </span>
                              <span className="text-gray-500 text-[8px]">{t("won")}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 text-xs py-8 bg-gray-800 rounded-md">
                      {t("noWinnersYet")}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {showWinToast && (
            <div className="fixed top-16 right-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg shadow-2xl animate-slide-down z-50">
              <div className="flex items-center gap-2">
                <span className="text-lg">🎉</span>
                <div>
                  <p className="font-bold text-xs">{t("youWonToast")}</p>
                  <p className="text-[10px]">+{lastWinAmount.toLocaleString()} ETB</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
        
        @keyframes slide-down { 
          from { transform: translateY(-20px); opacity: 0; } 
          to { transform: translateY(0); opacity: 1; } 
        }
        .animate-slide-down { animation: slide-down 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); }

        @keyframes lotteryTumble {
          0% { transform: rotateX(0deg) rotateY(0deg) rotateZ(0deg) scale(0.8); opacity: 0.5; }
          50% { transform: rotateX(180deg) rotateY(360deg) rotateZ(90deg) scale(1.3); opacity: 1; }
          100% { transform: rotateX(360deg) rotateY(720deg) rotateZ(360deg) scale(1); opacity: 1; }
        }

        .animate-tumble { animation: lotteryTumble 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; transform-style: preserve-3d; will-change: transform; }
      `}</style>
    </PageWrapper>
  );
};

export default FastKenoGamePage;